import * as Headless from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@oysterjs/ui/spinner';
import clsx from 'clsx';
import React from 'react';
import {
  AgencyUsageWindow,
  AgencyUserState,
  Role,
  useGetAgencyUsageHistoryQuery
} from '~/api/graphql';
import { useCurrentAgencyUser } from '~/components/current-user';
import { formatUsageUnits, parseUsageValue } from '~/lib/usage';
import { formatUsagePeriodRange, inclusivePeriodEnd, toDate } from './usage-utils';

const PERIOD_OPTIONS: { value: AgencyUsageWindow; label: string }[] = [
  { value: AgencyUsageWindow.LastDay, label: 'Last day' },
  { value: AgencyUsageWindow.Last_7Days, label: 'Last 7 days' },
  { value: AgencyUsageWindow.CurrentUsagePeriod, label: 'Current usage period' },
  { value: AgencyUsageWindow.PreviousUsagePeriod, label: 'Previous usage period' },
  { value: AgencyUsageWindow.Custom, label: 'Custom range' }
];

const TRIGGER_CLASS = clsx(
  'inline-flex items-center gap-2 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1.5',
  'text-xs font-medium text-neutral-700 dark:text-neutral-300',
  'hover:border-neutral-300 dark:hover:border-white/20 hover:bg-neutral-50 dark:hover:bg-white/5'
);

const PANEL_CLASS = clsx(
  'z-20 mt-1 w-72 rounded-xl bg-white dark:bg-neutral-800 p-2 shadow-lg',
  'origin-top transition data-[closed]:scale-95 data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in'
);

type UsageUser = {
  id: string;
  name: string;
  email: string;
  roles: Role[];
  state: AgencyUserState;
};

type UsageHistoryProps = {
  timezone: string;
  users: UsageUser[];
};

const parseDateInput = (value: string): Date | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return undefined;
  }
  return new Date(`${value}T00:00:00.000Z`);
};

const validateDateRange = (startDay: string, endDay: string) => {
  const startDate = parseDateInput(startDay);
  const endDate = parseDateInput(endDay);
  const days =
    startDate && endDate
      ? Math.floor((endDate.getTime() - startDate.getTime()) / 86_400_000) + 1
      : 0;
  const error =
    startDate && endDate && endDate < startDate
      ? 'End date must be on or after start date.'
      : days > 366
        ? 'Custom ranges cannot exceed 366 days.'
        : null;
  return {
    startDate,
    endDate,
    error,
    ready: !!startDate && !!endDate && days > 0 && !error
  };
};

const toDateInputValue = (value: Date | string | null | undefined): string => {
  const date = toDate(value);
  if (!date) {
    return '';
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type TimePeriodPickerProps = {
  window: AgencyUsageWindow;
  customStartDay: string;
  customEndDay: string;
  customRangeError: string | null;
  summary: string;
  onSelectWindow: (value: AgencyUsageWindow) => void;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
};

const TimePeriodPicker = ({
  window,
  customStartDay,
  customEndDay,
  customRangeError,
  summary,
  onSelectWindow,
  onStartChange,
  onEndChange
}: TimePeriodPickerProps) => (
  <Headless.Popover className="relative">
    {({ close }) => (
      <>
        <Headless.PopoverButton className={TRIGGER_CLASS} aria-label="Time period">
          <CalendarIcon className="size-4" />
          <span>{summary}</span>
          <ChevronDownIcon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
        </Headless.PopoverButton>
        <Headless.PopoverPanel transition anchor="bottom end" className={PANEL_CLASS}>
          <div className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Time period
          </div>
          <div className="flex flex-col">
            {PERIOD_OPTIONS.map((option) => {
              const selected = option.value === window;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onSelectWindow(option.value);
                    if (option.value !== AgencyUsageWindow.Custom) {
                      close();
                    }
                  }}
                  className={clsx(
                    'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm',
                    selected
                      ? 'bg-primary-50 dark:bg-primary-500/10 font-medium text-primary-900 dark:text-primary-200'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5'
                  )}
                >
                  <span>{option.label}</span>
                  {selected && (
                    <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                  )}
                </button>
              );
            })}
          </div>
          {window === AgencyUsageWindow.Custom && (
            <div className="mt-2 border-t border-neutral-100 dark:border-neutral-700 pt-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-xs text-neutral-600 dark:text-neutral-400">
                  <span>From</span>
                  <input
                    type="date"
                    value={customStartDay}
                    max={customEndDay || undefined}
                    onChange={(event) => onStartChange(event.target.value)}
                    className="rounded-md border border-neutral-300 dark:border-neutral-600 px-2 py-1 text-xs text-neutral-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-neutral-600 dark:text-neutral-400">
                  <span>To</span>
                  <input
                    type="date"
                    value={customEndDay}
                    min={customStartDay || undefined}
                    onChange={(event) => onEndChange(event.target.value)}
                    className="rounded-md border border-neutral-300 dark:border-neutral-600 px-2 py-1 text-xs text-neutral-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
              </div>
              {customRangeError && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                  {customRangeError}
                </p>
              )}
            </div>
          )}
        </Headless.PopoverPanel>
      </>
    )}
  </Headless.Popover>
);

export const UsageHistory = ({ timezone, users }: UsageHistoryProps) => {
  const currentUser = useCurrentAgencyUser();
  const isAdmin = currentUser.roles.includes(Role.Admin);
  const [window, setWindow] = React.useState(AgencyUsageWindow.Last_7Days);
  const [customStartDay, setCustomStartDay] = React.useState('');
  const [customEndDay, setCustomEndDay] = React.useState('');
  const isCustom = window === AgencyUsageWindow.Custom;
  const customRange = validateDateRange(customStartDay, customEndDay);
  const customRangeReady = !isCustom || customRange.ready;
  const { data, previousData, loading, error } = useGetAgencyUsageHistoryQuery({
    variables: {
      window,
      timezone,
      startDay: isCustom ? customRange.startDate : undefined,
      endDay: isCustom ? customRange.endDate : undefined
    },
    skip: !customRangeReady,
    notifyOnNetworkStatusChange: true
  });

  const queryData = data ?? previousData;
  const report = customRangeReady
    ? queryData?.agencyUser?.agency.subscription?.usageHistory
    : undefined;

  const seedCustomRangeIfEmpty = () => {
    if (customStartDay && customEndDay) {
      return;
    }
    if (report?.start == null || report?.end == null) {
      return;
    }
    const periodEnd = toDate(report.end);
    const start = toDateInputValue(report.start);
    const end = periodEnd ? toDateInputValue(inclusivePeriodEnd(periodEnd)) : '';
    if (!customStartDay && start) {
      setCustomStartDay(start);
    }
    if (!customEndDay && end) {
      setCustomEndDay(end);
    }
  };

  const onSelectWindow = (value: AgencyUsageWindow) => {
    setWindow(value);
    if (value === AgencyUsageWindow.Custom) {
      seedCustomRangeIfEmpty();
    }
  };

  const summary =
    window === AgencyUsageWindow.Custom
      ? (formatUsagePeriodRange(
          customRange.startDate,
          customRange.endDate ? new Date(customRange.endDate.getTime() + 86_400_000) : null,
          timezone
        ) ?? 'Custom range')
      : (PERIOD_OPTIONS.find((option) => option.value === window)?.label ?? 'Time period');

  const usedByUser = new Map(
    (report?.platformUnitsByUser ?? []).map((row) => [row.agencyUserId, parseUsageValue(row.used)])
  );
  const platformUnitsTotal = (report?.platformUnitsByUser ?? []).reduce(
    (sum, row) => sum + parseUsageValue(row.used),
    0
  );
  const listedUsers = users.filter(
    (user) => user.state === AgencyUserState.Active || (usedByUser.get(user.id) ?? 0) > 0
  );
  const visibleUsers = isAdmin
    ? [...listedUsers].sort(
        (a, b) =>
          (usedByUser.get(b.id) ?? 0) - (usedByUser.get(a.id) ?? 0) || a.name.localeCompare(b.name)
      )
    : [currentUser];

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">
              {isAdmin ? 'Usage by team member' : 'Your usage'}
            </h3>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Platform unit history
            </p>
          </div>
          <TimePeriodPicker
            window={window}
            customStartDay={customStartDay}
            customEndDay={customEndDay}
            customRangeError={customRange.error}
            summary={summary}
            onSelectWindow={onSelectWindow}
            onStartChange={setCustomStartDay}
            onEndChange={setCustomEndDay}
          />
        </div>
      </div>

      <div className="px-4 py-5 sm:px-6">
        {isCustom && !customRangeReady ? (
          <p
            className={`py-8 text-center text-sm ${
              customRange.error
                ? 'text-red-600 dark:text-red-400'
                : 'text-neutral-500 dark:text-neutral-400'
            }`}
          >
            {customRange.error ?? 'Choose a start and end date.'}
          </p>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-600 dark:text-red-400">
            Failed to load usage history.
          </p>
        ) : loading && !report ? (
          <div className="flex justify-center py-8">
            <Spinner size="size-6" color="neutral" />
          </div>
        ) : (
          <>
            {isAdmin && report?.agencyTotals && (
              <dl className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-md bg-neutral-50 dark:bg-neutral-900 px-4 py-3">
                  <dt className="text-sm text-neutral-500 dark:text-neutral-400">
                    Agency platform units
                  </dt>
                  <dd className="mt-1 text-xl font-semibold text-neutral-900 dark:text-white">
                    {formatUsageUnits(platformUnitsTotal)}
                  </dd>
                </div>
                <div className="rounded-md bg-neutral-50 dark:bg-neutral-900 px-4 py-3">
                  <dt className="text-sm text-neutral-500 dark:text-neutral-400">
                    Agency KB pages
                  </dt>
                  <dd className="mt-1 text-xl font-semibold text-neutral-900 dark:text-white">
                    {formatUsageUnits(parseUsageValue(report.agencyTotals.kbPages))}
                  </dd>
                </div>
                <div className="rounded-md bg-neutral-50 dark:bg-neutral-900 px-4 py-3">
                  <dt className="text-sm text-neutral-500 dark:text-neutral-400">
                    Agency voice minutes
                  </dt>
                  <dd className="mt-1 text-xl font-semibold text-neutral-900 dark:text-white">
                    {formatUsageUnits(parseUsageValue(report.agencyTotals.voiceMinutes))}
                  </dd>
                </div>
              </dl>
            )}

            <ul
              role="list"
              className={`divide-y divide-neutral-200 dark:divide-neutral-700 transition-opacity ${loading ? 'opacity-60' : ''}`}
            >
              {visibleUsers.map((user) => {
                const used = usedByUser.get(user.id) ?? 0;
                return (
                  <li
                    key={user.id}
                    className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0"
                  >
                    <div className="flex min-w-0 items-center gap-x-3">
                      <div className="flex size-10 flex-none items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-700 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                        {user.name
                          .split(' ')
                          .map((name) => name[0])
                          .join('')
                          .toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm/6 font-semibold text-neutral-900 dark:text-white">
                          {user.id === currentUser.id ? `${user.name} (you)` : user.name}
                        </p>
                        <p className="truncate text-xs/5 text-neutral-500 dark:text-neutral-400">
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <p className="shrink-0 text-sm font-semibold text-neutral-900 dark:text-white">
                      {formatUsageUnits(used)}
                    </p>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
